/**
 * One chronological spine for the Fundamentals screen.
 *
 * ## Why this exists
 *
 * The screen used to be four stacked sections: a calendar, a risk reading, and
 * a feed. A trader reading it had to hold three orderings in their head and
 * merge them mentally to answer one question — *what happened, and in what
 * order*. This builds that single ordering once, so the screen can draw it.
 *
 * Scheduled releases run FORWARD from now, published entries run BACK from it,
 * and the two are separated by a marker rather than by a heading. That is the
 * whole idea: the boundary between "coming" and "happened" is an instant on one
 * axis, not two lists that happen to sit near each other.
 *
 * Pure, per `packages/core` convention — the clock is passed in.
 */
import type { ReleaseImportance } from './types.js';

/** Anything with a time, a class, and a way to say whether it is the user's. */
export interface TimelineInput {
  /** ISO. For a scheduled release this is in the future. */
  readonly at: string;
  readonly importance: ReleaseImportance;
  /**
   * The topics this touches. Compared against the caller's traded symbols to
   * decide whether the entry gets the relevance rail.
   */
  readonly affects: readonly string[];
  /**
   * Stories carried by other sources, if this is a headline.
   *
   * Present here rather than left to the caller because clustering has to
   * happen against the SAME ordering the spine uses — grouping after the sort
   * would let a cluster's members straddle the NOW marker.
   */
  readonly alsoCarriedBy?: readonly string[];
}

/** Where an entry sits relative to now. */
export type TimelineSide = 'scheduled' | 'published';

export interface TimelineRow<T> {
  readonly entry: T;
  readonly side: TimelineSide;
  readonly atMs: number;
  /** True when the entry touches something the caller trades. */
  readonly mine: boolean;
  /**
   * Other entries carrying the same story, newest first.
   *
   * Empty for everything except the head of a cluster. A row with a non-empty
   * `carriedWith` is drawn as one collapsed line rather than as N rows, which
   * is the difference between a readable spine and six near-identical lines
   * burying the release under them.
   */
  readonly carriedWith: readonly T[];
}

export interface TimelineOptions {
  readonly now: Date;
  /** Upper-cased traded symbols. Empty for an account with no journal yet. */
  readonly traded?: ReadonlySet<string>;
}

/** Does this entry touch anything the caller actually trades? */
function isMine(entry: TimelineInput, traded: ReadonlySet<string>): boolean {
  /* A short circuit, not a guard: `.some()` over an empty Set is already false
     for every element. Mutation testing confirmed removing this line changes no
     observable behaviour — kept for the statement of intent, and recorded here
     so nobody writes a test that cannot fail trying to defend it. */
  if (traded.size === 0) return false;
  return entry.affects.some((a) => traded.has(a.toUpperCase()));
}

/**
 * Build the spine.
 *
 * Order is: scheduled entries SOONEST-first, then published entries
 * NEWEST-first. Reading top to bottom you walk toward now from the future,
 * cross the marker, and walk away from it into the past — so the two entries
 * either side of the marker are the two nearest in time, which is the only
 * arrangement where the marker means anything.
 *
 * ## Clustering
 *
 * Entries naming each other in `alsoCarriedBy` collapse into the FIRST one
 * reached in that order. First rather than "the primary source" on purpose: the
 * ordering already encodes what the screen cares about, and picking a different
 * head would put the cluster somewhere other than where its story broke.
 *
 * An unparseable timestamp drops the entry rather than sorting it to an
 * arbitrary end — `Date.parse` returns NaN, NaN loses every comparison, and an
 * entry at an unknown time on a time axis is not information.
 */
export function buildTimeline<T extends TimelineInput>(
  entries: readonly T[],
  opts: TimelineOptions,
): readonly TimelineRow<T>[] {
  const nowMs = opts.now.getTime();
  const traded = opts.traded ?? new Set<string>();

  const dated = entries
    .map((entry) => ({ entry, atMs: Date.parse(entry.at) }))
    /* NaN loses every comparison, so an unfiltered bad timestamp is neither
       `> nowMs` nor `<= nowMs` and vanishes into neither partition. Mutation
       testing confirmed removing this line changes no observable behaviour —
       it is kept because the entry disappearing ON PURPOSE and disappearing by
       accident are different pieces of code to inherit. */
    .filter((r) => Number.isFinite(r.atMs));

  const scheduled = dated
    .filter((r) => r.atMs > nowMs)
    .sort((a, b) => a.atMs - b.atMs);
  const published = dated
    .filter((r) => r.atMs <= nowMs)
    .sort((a, b) => b.atMs - a.atMs);

  const ordered = [
    ...scheduled.map((r) => ({ ...r, side: 'scheduled' as const })),
    ...published.map((r) => ({ ...r, side: 'published' as const })),
  ];

  /* Two passes, because a cluster head is not necessarily reached first.

     Published entries sort newest-first and the outlet that BREAKS a story
     publishes before the ones that pick it up — so the originator, which is the
     entry carrying `alsoCarriedBy`, sorts LAST of its own cluster. Emitting
     rows in one pass therefore drew the carriers as their own rows before ever
     reaching the head that claims them, and a story six outlets ran became six
     rows: exactly the pile the collapse exists to prevent.

     Pass 1 decides ownership for the whole list. Pass 2 emits, skipping
     anything already claimed. Order is unaffected — `ordered` is not
     re-sorted, and a head still appears at its own position. */
  const taken = new Set<number>();
  const claimed = new Map<number, T[]>();

  for (let i = 0; i < ordered.length; i += 1) {
    if (taken.has(i)) continue;
    const head = ordered[i];
    if (head === undefined) continue;

    const others = head.entry.alsoCarriedBy ?? [];
    if (others.length === 0) continue;

    const wanted = new Set(others.map((s) => s.toUpperCase()));
    const carriedWith: T[] = [];
    for (let j = 0; j < ordered.length; j += 1) {
      if (j === i || taken.has(j)) continue;
      const cand = ordered[j];
      if (cand === undefined) continue;
      /* Match on the SAME side only. A story that broke before now and a
         scheduled release that mentions the same source are not one event,
         and folding them would move an entry across the marker. */
      if (cand.side !== head.side) continue;
      const src = sourceOf(cand.entry);
      if (src !== null && wanted.has(src.toUpperCase())) {
        carriedWith.push(cand.entry);
        taken.add(j);
      }
    }
    if (carriedWith.length > 0) claimed.set(i, carriedWith);
  }

  const rows: TimelineRow<T>[] = [];
  for (let i = 0; i < ordered.length; i += 1) {
    if (taken.has(i)) continue;
    const head = ordered[i];
    if (head === undefined) continue;
    rows.push({
      entry: head.entry,
      side: head.side,
      atMs: head.atMs,
      mine: isMine(head.entry, traded),
      carriedWith: claimed.get(i) ?? [],
    });
  }

  return rows;
}

/**
 * The publishing source of an entry, when it has one.
 *
 * Structural rather than typed: a release card and a news item both carry a
 * `sourceId` and this module deliberately knows about neither shape.
 */
function sourceOf(entry: unknown): string | null {
  if (typeof entry !== 'object' || entry === null) return null;
  const v = (entry as { sourceId?: unknown }).sourceId;
  return typeof v === 'string' ? v : null;
}

/**
 * How many entries the filter would keep.
 *
 * Exported so the screen can report "9 entries" beside the filter without
 * running the filter twice, and so the count and the list can never disagree.
 */
export function countBy<T>(
  rows: readonly TimelineRow<T>[],
  predicate: (r: TimelineRow<T>) => boolean,
): number {
  let n = 0;
  for (const r of rows) if (predicate(r)) n += 1;
  return n;
}
